import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const AUTO_PLAY_INTERVAL = 5000;

const slides = [
  {
    title: '55漫画网',
    subtitle: '韩漫全集免费阅读',
    description: '海量韩漫、日漫、国漫资源，高清画质，极速加载，无需付费即可畅享全部内容',
    cta: '立即阅读',
    ctaLink: '/category/korean',
    bg: 'https://readdy.ai/api/search-image?query=A%20stunning%20dramatic%20Korean%20manhwa%20style%20illustration%20featuring%20emotional%20characters%20in%20a%20romantic%20scene%20with%20warm%20golden%20sunset%20lighting%2C%20soft%20bokeh%20effects%2C%20modern%20Korean%20digital%20art%20aesthetic%2C%20dreamy%20atmosphere%2C%20clean%20composition%20with%20ample%20negative%20space%20at%20the%20top%2C%20muted%20warm%20tones%2C%20cinematic%20quality&width=1600&height=800&seq=hero-bg-001&orientation=landscape',
  },
  {
    title: '猎魔人传说',
    subtitle: '燃爆热血奇幻巨作',
    description: '累计阅读超2150万次，年度最受欢迎的韩漫作品之一',
    cta: '开始阅读',
    ctaLink: '/comic/demon-hunter',
    bg: 'https://readdy.ai/api/search-image?query=An%20epic%20fantasy%20Korean%20manhwa%20battle%20scene%20with%20a%20warrior%20facing%20demons%20in%20a%20dramatic%20landscape%2C%20intense%20action%20composition%2C%20dark%20moody%20lighting%20with%20warm%20fire%20accents%2C%20Korean%20digital%20illustration%20style%2C%20cinematic%20wide%20shot%2C%20epic%20scale%2C%20atmospheric%20with%20dark%20upper%20area&width=1600&height=800&seq=hero-bg-002&orientation=landscape',
  },
  {
    title: '秘密花园',
    subtitle: '悬疑奇幻爱情故事',
    description: '月圆之夜的花园中隐藏着跨越百年的秘密，等你来揭开',
    cta: '一探究竟',
    ctaLink: '/comic/secret-garden',
    bg: 'https://readdy.ai/api/search-image?query=A%20mysterious%20moonlit%20garden%20scene%20in%20Korean%20manhwa%20art%20style%2C%20glowing%20flowers%20at%20night%2C%20a%20woman%20in%20elegant%20dress%20walking%20through%20the%20garden%2C%20atmospheric%20fantasy%20vibe%2C%20soft%20moonlight%2C%20magical%20particles%2C%20vertical%20composition%2C%20dreamy%20aesthetic%2C%20dark%20upper%20portion%20for%20text%20overlay&width=1600&height=800&seq=hero-bg-003&orientation=landscape',
  },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  const goTo = useCallback((index: number) => {
    setCurrentSlide(index);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, []);

  const goNext = useCallback(() => {
    goTo((currentSlide + 1) % slides.length);
  }, [currentSlide, goTo]);

  const goPrev = useCallback(() => {
    goTo((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goTo]);

  useEffect(() => {
    if (isPaused) return;

    timerRef.current = setInterval(() => {
      goNext();
    }, AUTO_PLAY_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, goNext]);

  useEffect(() => {
    if (isPaused) return;

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / AUTO_PLAY_INTERVAL) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        progressRef.current = requestAnimationFrame(animate);
      }
    };

    progressRef.current = requestAnimationFrame(animate);

    return () => {
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
    };
  }, [isPaused, currentSlide]);

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false);
        startTimeRef.current = Date.now();
      }}
    >
      {slides.map((slide, i) => {
        const isActive = i === currentSlide;
        const isLeaving = !isActive;
        return (
          <div
            key={i}
            className={`absolute inset-0 transition-all duration-1000 ease-out ${
              isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <img
              src={slide.bg}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/55"></div>
          </div>
        );
      })}

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full text-center px-4 md:px-6 max-w-3xl">
          {slides.map((slide, i) => (
            <div
              key={i}
              className={`transition-all duration-700 ease-out ${
                i === currentSlide
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-6 absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none'
              }`}
            >
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3 leading-tight">
                {slide.title}
              </h1>
              <p className="text-lg md:text-xl text-background-100 font-light mb-2">
                {slide.subtitle}
              </p>
              <p className="text-sm md:text-base text-foreground-300 mb-8 max-w-xl mx-auto">
                {slide.description}
              </p>
              <Link
                to={slide.ctaLink}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-background-50/30 bg-background-50/10 backdrop-blur-sm text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/20 transition-colors cursor-pointer"
              >
                {slide.cta}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="relative h-2 rounded-full transition-all duration-300 cursor-pointer overflow-hidden bg-background-50/30"
            style={{ width: i === currentSlide ? '40px' : '8px' }}
            aria-label={`Slide ${i + 1}`}
          >
            {i === currentSlide && (
              <span
                className="absolute inset-0 rounded-full bg-background-50 transition-none"
                style={{ width: `${progress}%` }}
              />
            )}
          </button>
        ))}
      </div>

      <button
        onClick={goPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/20 transition-all duration-300 opacity-0 group-hover:opacity-100 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={goNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/10 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/20 transition-all duration-300 opacity-0 group-hover:opacity-100 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}